/**
 * Links between Planosfera resources are ordinary addresses of the resource's canonical page
 * (`/app/boards/<id>`, `/app/documents/<id>`, ...), made absolute with this site's origin. They
 * carry the resource's stable id, never its name, so a rename or a move (same id) keeps them
 * working, and they work anywhere a link can be pasted. Opening one is a normal navigation: the
 * page itself decides what the viewer may see, and shows "not found" for a deleted or
 * inaccessible target - a link never reveals the target's name or any other metadata.
 */

/** The address of an in-app path, absolute (`/app/boards/1` -> `https://host/app/boards/1`). */
export function absoluteUrl(path: string, origin: string = window.location.origin): string {
  return new URL(path, origin).toString();
}

/**
 * The in-app path (`/app/...` with its query and hash) a link points to, or `null` when it is
 * external, malformed or leads outside the application.
 */
export function internalPath(
  href: string | null | undefined,
  origin: string = window.location.origin,
): string | null {
  if (!href) return null;
  try {
    const url = new URL(href, origin);
    if (url.origin !== new URL(origin).origin) return null;
    if (url.pathname !== '/app' && !url.pathname.startsWith('/app/')) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Put text on the clipboard; `false` when the browser refuses (no permission, insecure page). */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
